import { type components } from '@shared/api';

/**
 * The closed sets of the journal, and the sentence key each value renders as.
 *
 * Keys, never sentences (`rules/i18n.mdc` §5), and every map is a `Record` over the contract's own
 * union — so an outcome the specification adds is a compile error here rather than a blank cell in a
 * record an auditor is reading.
 *
 * **The provider-kind and task-kind maps repeat the ones in `units/ai-provider`, and that is
 * deliberate.** The journal does not reference a provider: `providerName` and `providerKind` are
 * **copied onto the row** at write time precisely so a five-year-old entry stays readable after the
 * provider it names has been deleted. Reaching into another unit to label a copied column would make
 * this unit depend on the lifetime of a row it does not have. The catalogue keys are shared, so the
 * two maps cannot describe the same value differently — what is duplicated is the mapping, not the
 * text.
 */

type Outcome = components['schemas']['ExternalCallOutcome'];

type ProviderKind = components['schemas']['AiProviderKind'];

type TaskKind = components['schemas']['AiTaskKind'];

export const EXTERNAL_CALL_OUTCOMES = [
  'COMPLETED',
  'REFUSED_BY_PERIMETER',
  'REFUSED_BY_BUDGET',
  'PROVIDER_FAILED',
  'CANCELLED',
] as const satisfies readonly Outcome[];

/**
 * A refusal is as much of a record as a completed call.
 *
 * The journal answers «что уходило наружу», and an attempt the perimeter gate stopped is part of
 * that answer: it says the boundary was reached and held. Four of the five values below are
 * therefore not failures of the journal — they are what it exists to show.
 */
export const EXTERNAL_CALL_OUTCOME_LABEL: Readonly<Record<Outcome, string>> = {
  COMPLETED: 'ai.externalCalls.outcome.completed',
  REFUSED_BY_PERIMETER: 'ai.externalCalls.outcome.refusedByPerimeter',
  REFUSED_BY_BUDGET: 'ai.externalCalls.outcome.refusedByBudget',
  PROVIDER_FAILED: 'ai.externalCalls.outcome.providerFailed',
  CANCELLED: 'ai.externalCalls.outcome.cancelled',
};

/** The one outcome that means material actually left. Everything else is a boundary that held. */
export const EXTERNAL_CALL_COMPLETED: Outcome = 'COMPLETED';

export const EXTERNAL_CALL_PROVIDER_KIND_LABEL: Readonly<Record<ProviderKind, string>> = {
  OPENROUTER: 'ai.provider.kind.openrouter',
  OPENAI_COMPAT: 'ai.provider.kind.openaiCompat',
  OPENAI: 'ai.provider.kind.openai',
  ANTHROPIC: 'ai.provider.kind.anthropic',
};

export const EXTERNAL_CALL_TASK_KIND_LABEL: Readonly<Record<TaskKind, string>> = {
  LAW_COMPLIANCE_CHECK: 'ai.task.lawComplianceCheck',
  PROCEDURE_EXTRACTION: 'ai.task.procedureExtraction',
  CONTRACT_VS_STANDARD: 'ai.task.contractVsStandard',
  ASSISTANT_ANSWER: 'ai.task.assistantAnswer',
  EMBEDDING: 'ai.task.embedding',
  CONNECTION_CHECK: 'ai.task.connectionCheck',
  // The settings form asking the provider which models exist. It carries no customer material —
  // the row's «передано» is zero documents — but it is a crossing, and the journal answers «what
  // left the building», not «what it cost».
  MODEL_CATALOG: 'ai.task.modelCatalog',
};
